import { ChangeDetectorRef, OnDestroy, OnInit, } from '@angular/core';
import { Router } from '@angular/router';
import { AppStateFacade, Setting, } from '@app-state';
import { PageMetadataRegistry } from '@app/router';
import { ChartLineStyle, ChartTooltipTrigger, } from '@components/ui-chart/base-chart.types';
import { enumMapper, FolderType, Interval, JobState, ROBOT_STATE_FILTER, RobotState, } from '@models';
import { TranslateService } from '@ngx-translate/core';
import { encodeFilters } from '@pages/generics/grid';
import { DEFAULT_TRANSACTION_INTERVALS, intervalToUnit, unitToFormat, } from '@pages/shared/chart.config';
import { ChartColorService, ChartService, ResourceMapperService, SignalRService, StatsService, StorageService, TransactionService, } from '@services';
import { Logger } from '@services/logger/abstract.logger';
import { unsubscribe } from '@utils/object.utils';
import cloneDeep from 'lodash-es/cloneDeep';
import values from 'lodash-es/values';
import * as moment from 'moment';
import { BehaviorSubject, combineLatest, iif, interval, merge, of, Subject, } from 'rxjs';
import { catchError, debounceTime, delay, filter, finalize, map, retryWhen, switchMap, switchMapTo, takeUntil, tap, throttleTime, } from 'rxjs/operators';
// FIXME: expose these from a common source
var ROBOT_STATES = enumMapper(RobotState, {
    resourceKey: 'RobotState',
    filterValueMap: ROBOT_STATE_FILTER,
});
// FIXME: expose these from a common source
var JOB_STATES = enumMapper(JobState, {
    resourceKey: 'JobState',
});
var StatsPageComponent = /** @class */ (function () {
    function StatsPageComponent(_appState, _pageRegistry, _statsService, _transactionService, _chartService, _signalRService, _translateService, _router, _cd, _logger, _storageService, _chartColorService) {
        var _this = this;
        this._appState = _appState;
        this._pageRegistry = _pageRegistry;
        this._statsService = _statsService;
        this._transactionService = _transactionService;
        this._chartService = _chartService;
        this._signalRService = _signalRService;
        this._translateService = _translateService;
        this._router = _router;
        this._cd = _cd;
        this._logger = _logger;
        this._storageService = _storageService;
        this._chartColorService = _chartColorService;
        this.ChartLineStyle = ChartLineStyle;
        this.ChartTooltipTrigger = ChartTooltipTrigger;
        this.defaultTransactionIntervals = DEFAULT_TRANSACTION_INTERVALS;
        this.FolderType = FolderType;
        this.folderType$ = this._appState.user.folder.type$;
        this.countStatistics = [{
                title: 'Processes',
            }, {
                title: 'Assets',
            }, {
                title: 'Queues',
            }, {
                title: 'Triggers',
            }];
        this.sessionChart = {
            loading$: new BehaviorSubject(true),
            hasAccess$: new BehaviorSubject(false),
            hasData$: new BehaviorSubject(false),
            data: null,
            legend: null,
            colors: this._chartColorService.session,
        };
        this.jobChart = {
            loading$: new BehaviorSubject(true),
            hasAccess$: new BehaviorSubject(false),
            hasData$: new BehaviorSubject(false),
            data: null,
            legend: null,
            colors: this._chartColorService.jobs,
        };
        this.transactionChart = {
            loading$: new BehaviorSubject(true),
            hasAccess$: new BehaviorSubject(false),
            hasData$: new BehaviorSubject(false),
            data: null,
            legend: null,
            labels: null,
            colors: this._chartColorService.transactions,
        };
        this._labelFormat = '';
        this._stateLabels = {
            sessions: values(ResourceMapperService.sessionStatus),
            jobs: values(ResourceMapperService.jobStatus),
        };
        this._timeoutInterval = 15 * 1000;
        this._destroyed$ = new Subject();
        this._refreshSessionChart = function (_a) {
            var counts = _a[0], translations = _a[1];
            var countData = [];
            var actualLabels = [];
            _this.sessionChart.hasData$.next(false);
            counts.forEach(function (_a) {
                var _b = _a.count, count = _b === void 0 ? 0 : _b, title = _a.title;
                countData.push(count);
                actualLabels.push(ResourceMapperService.sessionStatus[title]);
                if (count > 0) {
                    _this.sessionChart.hasData$.next(true);
                }
            });
            _this.sessionChart.data = countData.map(function (value, index) { return ({
                key: actualLabels[index],
                value: value,
            }); });
            _this.sessionChart.legend = actualLabels.map(function (val) { return translations[val]; });
            _this.sessionChart.loading$.next(false);
        };
        this._refreshJobChart = function (_a) {
            var counts = _a[0], translations = _a[1];
            var countData = [];
            var actualLabels = [];
            _this.jobChart.hasData$.next(false);
            counts.forEach(function (_a) {
                var _b = _a.count, count = _b === void 0 ? 0 : _b, title = _a.title;
                countData.push(count);
                actualLabels.push(ResourceMapperService.jobStatus[title]);
                if (count > 0) {
                    _this.jobChart.hasData$.next(true);
                }
            });
            _this.jobChart.data = countData.map(function (value, index) { return ({
                key: actualLabels[index],
                value: value,
            }); });
            _this.jobChart.legend = actualLabels.map(function (val) { return translations[val]; });
            _this.jobChart.loading$.next(false);
        };
        this._initializePieChart$ = function (trigger$, permission, chart, labels, fetch) {
            // only trigger if we have access, otherwise update the loading state
            var triggers = trigger$.pipe(switchMapTo(_this._appState.user.permissions.isGranted(permission)), tap(function (hasAccess) {
                if (!hasAccess) {
                    chart.loading$.next(false);
                }
                chart.hasAccess$.next(hasAccess);
            }), filter(Boolean));
            // fetch at most 1 request / second
            var source = merge(triggers, triggers
                .pipe(debounceTime(900))).pipe(throttleTime(1000), switchMapTo(fetch.pipe(catchError(function () { return of([]); }))));
            return combineLatest([
                source,
                _this._translateService.stream(labels),
            ]);
        };
        var userId = this._appState.user.value.id;
        this._intervalCacheKey = this._router.url + " chart - " + userId + " - Transactions";
        this.transactionInterval$ = new BehaviorSubject(this._storageService.get(this._intervalCacheKey, Interval.Day));
        this._appState.user.folder.change$
            .pipe(takeUntil(this._destroyed$))
            .subscribe(function () {
            _this._loadDashboardCount();
            _this.loadTransactionChart(_this.transactionInterval$.value);
        });
        this.hiddenCountStatistics$ = this._appState.user.folder.isClassic$
            .pipe(map(function (isClassic) { return isClassic
            ? []
            : ['Triggers']; }), takeUntil(this._destroyed$));
        this.countStatistics.forEach(function (item) {
            item.page = _this._pageRegistry.findByTitle(item.title);
        });
    }
    Object.defineProperty(StatsPageComponent.prototype, "labelFormat", {
        get: function () {
            return this._labelFormat;
        },
        set: function (value) {
            this._labelFormat = unitToFormat(value);
        },
        enumerable: true,
        configurable: true
    });
    StatsPageComponent.prototype.ngOnInit = function () {
        var _this = this;
        this._initializePieChart$(merge(of(void 0), this._signalRService.sessions$, this._appState.user.folder.change$), 'Robots.View', this.sessionChart, this._stateLabels.sessions, this._statsService.getSessionsStats()).pipe(takeUntil(this._destroyed$)).subscribe(this._refreshSessionChart);
        this._initializePieChart$(merge(of(void 0), this._signalRService.jobs$, this._appState.user.folder.change$), 'Jobs.View', this.jobChart, this._stateLabels.jobs, this._statsService.getJobsStats()).pipe(takeUntil(this._destroyed$)).subscribe(this._refreshJobChart);
        this.intervalLabel$ = merge(this._appState.language.change$, this.transactionInterval$).pipe(map(function (_) { return _this._chartService.getTransactionsLabelForInterval(_this.transactionInterval$.value); }));
        this._signalRService.watchAllJobs();
        this._signalRService.watchAllRobots();
        this._loadDashboardCount();
        this.loadTransactionChart(this.transactionInterval$.value);
    };
    StatsPageComponent.prototype.ngOnDestroy = function () {
        unsubscribe(this._dashboardCountSub, this._transactionSub);
        this._signalRService.watchAllJobs(false);
        this._signalRService.watchAllRobots(false);
        this._destroyed$.next();
    };
    StatsPageComponent.prototype.onIntervalChange = function (transactionInterval) {
        // if not filter is saved in localStorage, use the default
        if (transactionInterval !== null) {
            this.transactionInterval$.next(transactionInterval);
        }
        this.loadTransactionChart(this.transactionInterval$.value);
        this._storageService.set(this._intervalCacheKey, this.transactionInterval$.value);
    };
    StatsPageComponent.prototype.onLabelClick = function (type, translationKey) {
        var state;
        if (type === 'robots') {
            state = ROBOT_STATES.find(function (r) { return r.translationKey === translationKey; });
        }
        else if (type === 'jobs') {
            state = JOB_STATES.find(function (j) { return j.translationKey === translationKey; });
        }
        else {
            throw Error("unkown type: " + type);
        }
        this._router.navigate(["/" + type], {
            queryParams: encodeFilters({ state: state }),
        });
    };
    StatsPageComponent.prototype.loadTransactionChart = function (transactionInterval) {
        var _this = this;
        var transactionLabels = values(ResourceMapperService.transactionStatus);
        this.transactionChart.hasData$.next(false);
        this.transactionChart.loading$.next(true);
        this.labelFormat = intervalToUnit(transactionInterval);
        if (this._transactionSub) {
            this._transactionSub.unsubscribe();
        }
        this._transactionSub =
            this._appState.user.permissions.isGranted('Queues.View&Transactions.View')
                .pipe(tap(function (hasAccess) {
                if (!hasAccess) {
                    _this.transactionChart.loading$.next(false);
                }
                _this.transactionChart.hasAccess$.next(hasAccess);
            }), filter(function (hasAccess) { return hasAccess; }), switchMap(function () {
                return combineLatest([
                    iif(function () { return _this._appState.user.folder.hasAny; }, _this._transactionService
                        .getTransactionStats(transactionInterval, 0), of(null)).pipe(catchError(function () { return of(null); }), finalize(function () { return _this.transactionChart.loading$.next(false); })),
                    _this._translateService
                        .stream(transactionLabels),
                ]);
            }))
                .subscribe(function (_a) {
                var result = _a[0], labelTranslations = _a[1];
                if (!result) {
                    _this._logger.error('An error has occurred while fetching the network response');
                    return;
                }
                var chartData = cloneDeep(result.chartData);
                // Chart lines are rendered in the order that they are in this array. Reverse them so the totals line is rendered
                // first, not last (i.e. so it no longer covers the other lines).
                chartData.reverse();
                var chartLabels = chartData
                    .map(function (entry) { return ResourceMapperService.transactionStatus[entry.label]; });
                _this.transactionChart.legend = [];
                _this.transactionChart.data = [];
                chartData = chartData.map(function (entry, idx) {
                    entry.label = labelTranslations[chartLabels[idx]];
                    _this.transactionChart.legend[idx] = labelTranslations[chartLabels[idx]];
                    return entry;
                });
                chartData.forEach(function (dataset) {
                    _this.transactionChart.data.push(dataset.data);
                });
                _this.transactionChart.labels = result
                    .chartLabels
                    .map(function (label) { return moment(label)
                    .tz(_this._appState.settings.get(Setting.timezone))
                    .format(_this.labelFormat); });
                _this.transactionChart.hasData$.next(result.hasData);
            });
    };
    StatsPageComponent.prototype.getLargeScreenSize = function (disabledCount) {
        return Math.floor(100 / (this.countStatistics.length - disabledCount));
    };
    StatsPageComponent.prototype.getMediumScreenSize = function (disabledCount) {
        return (this.countStatistics.length - disabledCount) % 2 ? 33 : 50;
    };
    StatsPageComponent.prototype._loadDashboardCount = function () {
        var _this = this;
        if (this._dashboardCountSub) {
            this._dashboardCountSub.unsubscribe();
        }
        this._dashboardCountSub =
            merge(this._appState.user.permissions.value$, interval(this._timeoutInterval))
                .pipe(switchMap(function () { return _this._statsService.getCountStats(); }), retryWhen(function (error) {
                return delay(_this._timeoutInterval)(error);
            }))
                .subscribe(function (counts) {
                (counts || []).forEach(function (count, idx) {
                    count.page = _this.countStatistics[idx].page;
                    _this.countStatistics[idx] = count;
                });
                _this._cd.markForCheck();
            });
    };
    return StatsPageComponent;
}());
export { StatsPageComponent };
